import * as Colors from '@brightlayer-ui/colors';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertType, useAlert } from '../lib/AlertContext';

const styles = {
    dialogBlock: {
        backgroundColor: Colors.blue[700],
        color: Colors.white[50],
    },
    inputBox: {
        margin: 2,
        fontWeight: 'bold',
    },
    inputLabel: {
        min: 10,
    },
    infoOuterBox: {
        alignContent: 'center',
        textAlign: 'center',
        marginBottom: 2,
    },
    contentContainer: {
        marginBottom: '20px',
    },
    button: {
        margin: '12px',
    },
    actionsContainer: {
        display: 'flex',
        width: '100%',
    },
    leftActions: {
        display: 'flex',
        alignItems: 'center',
        margin: '16px',
        marginRight: 'auto',
    },
    rightActions: {
        display: 'flex',
        margin: '16px',
        marginLeft: 'auto',
    },
    icon: {
        width: 80,
        height: 80,
        borderRadius: 50,
        bgcolor: Colors.blue[50],
        margin: '0 auto',
        marginTop: 4,
    },
};

export const AlertModal: React.FC = () => {
    const { clearAlert, alert } = useAlert();
    const { t } = useTranslation();

    const [loadingState, setLoadingState] = useState(false);
    const [error, setError] = useState(false);

    const onClickRightButton = async (a: Alert): Promise<void> => {
        setLoadingState(true);

        try {
            if (a.type === AlertType.CONFIRMATION || a.type === AlertType.ERROR) {
                await a.onConfirm?.();
            }
            if (a.type === AlertType.INFORMATIONAL) {
                await a.onDismiss?.();
            }
            clearAlert(a);
        } catch {
            setError(true);
        } finally {
            setLoadingState(false);
        }
    };

    const isError = (error && alert?.type === AlertType.CONFIRMATION) || alert?.type === AlertType.ERROR;
    const iconPresent = alert?.icon;
    const showCancelButton =
        alert?.type === AlertType.CONFIRMATION || (alert?.type === AlertType.ERROR && alert.cancelText);

    return alert ? (
        <>
            <Dialog open={true} maxWidth="sm" data-testid={alert.testId ?? 'alert-modal'}>
                {iconPresent && (
                    <Box
                        data-testid="icon"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={[styles.icon, alert.iconBGColor ? { bgcolor: alert.iconBGColor } : {}]}
                    >
                        {alert.icon}
                    </Box>
                )}
                <DialogTitle sx={{ margin: iconPresent ? '0 auto' : '0' }}>
                    {isError ? alert.errorTitle : alert.title}
                </DialogTitle>
                <DialogContent sx={{ ...styles.contentContainer, ...(isError ? { color: Colors.red[400] } : {}) }}>
                    {isError ? alert.errorDescription : alert.description}
                </DialogContent>
                <Divider />
                <DialogActions sx={styles.actionsContainer}>
                    {showCancelButton && (
                        <Box sx={styles.leftActions}>
                            <Button
                                data-testid="alert-modal-cancel-button"
                                variant="outlined"
                                disabled={loadingState}
                                onClick={(): void => {
                                    void alert.onCancel?.();
                                    setError(false);
                                    clearAlert();
                                }}
                            >
                                {alert.cancelText}
                            </Button>
                        </Box>
                    )}
                    <Box sx={styles.rightActions}>
                        <Button
                            data-testid="alert-modal-ok-button"
                            variant="contained"
                            disabled={loadingState}
                            onClick={(): void => {
                                void onClickRightButton(alert);
                            }}
                        >
                            {loadingState ? (
                                <CircularProgress data-testid="alert-modal-loading" size="1.5rem" color="inherit" />
                            ) : error ? (
                                t('COMMON.TRY_AGAIN')
                            ) : alert.type === AlertType.CONFIRMATION || alert.type === AlertType.ERROR ? (
                                alert.confirmText
                            ) : (
                                alert.clearAlertText ?? t('COMMON.OK')
                            )}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    ) : null;
};
